import { motion } from 'framer-motion';
import { ShieldAlert, CheckCircle2 } from 'lucide-react';
import { ConfidenceBar } from '@/components/profiler/ConfidenceBar';
import type { DossierSectionProps } from './types';
import type { MatchType, SanctionsHit } from '@/types/profiler';

// ── match type badge ──────────────────────────────────────────────────────────

const MATCH_COLOR: Record<MatchType, string> = {
  EXACT: '#FF1F1F',
  CLOSE: '#FF6B1A',
  WEAK:  '#F0B429',
};

function MatchTypeBadge({ matchType }: { matchType: MatchType }) {
  const color = MATCH_COLOR[matchType];
  return (
    <span
      style={{
        background: `${color}18`,
        color,
        border: `1px solid ${color}35`,
        fontSize: 8,
        fontFamily: 'monospace',
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 99,
        whiteSpace: 'nowrap',
      }}
    >
      {matchType}
    </span>
  );
}

// ── list chip ─────────────────────────────────────────────────────────────────

function ListChip({ name }: { name: string }) {
  return (
    <span
      style={{
        background: '#FF1F1F10',
        color: '#FF6B1A',
        border: '1px solid #FF6B1A20',
        fontSize: 8,
        fontFamily: 'monospace',
        padding: '1px 6px',
        borderRadius: 99,
      }}
    >
      {name}
    </span>
  );
}

// ── screened list chip ────────────────────────────────────────────────────────

function ScreenedChip({ name }: { name: string }) {
  return (
    <span
      style={{
        background: '#0C1118',
        color: '#5A7A9A',
        border: '1px solid #182030',
        fontSize: 8,
        fontFamily: 'monospace',
        padding: '3px 8px',
        borderRadius: 99,
      }}
    >
      {name}
    </span>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export function Sanctions({ pipeline }: DossierSectionProps) {
  const hits: SanctionsHit[] = pipeline?.sanctions?.hits ?? [];
  const highestScore = pipeline?.sanctions?.highest_score ?? 0;
  const listCoverage: string[] = pipeline?.sanctions?.list_coverage ?? [];
  const hasCriticalHit = hits.some(h => h.score >= 0.7);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {/* Critical alert block */}
      {hasCriticalHit && (
        <div
          style={{
            background: '#1A0000',
            border: '1px solid #FF1F1F60',
            borderRadius: 8,
            padding: '16px 18px',
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <ShieldAlert size={18} style={{ color: '#FF1F1F', flexShrink: 0 }} />
            <span style={{ color: '#FF1F1F', fontSize: 13, fontFamily: 'monospace', fontWeight: 800, letterSpacing: '0.1em' }}>
              MLRO ACTION REQUIRED
            </span>
            <span
              style={{
                background: '#FF1F1F25',
                color: '#FF1F1F',
                border: '1px solid #FF1F1F50',
                fontSize: 8,
                fontFamily: 'monospace',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 99,
                marginLeft: 'auto',
              }}
            >
              CRITICAL MATCH
            </span>
          </div>
          <p style={{ color: '#FF6B1A', fontSize: 10, fontFamily: 'monospace', lineHeight: 1.6 }}>
            One or more sanctions matches exceed the 70% confidence threshold.
            This case requires immediate MLRO review. A Suspicious Transaction Report (STR)
            filing must be considered under PMLA S.3 read with PMLA Rules 2005.
            Do not tip off the subject.
          </p>
        </div>
      )}

      {/* No hits */}
      {hits.length === 0 && (
        <div
          style={{
            background: '#001A08',
            border: '1px solid #00C85330',
            borderRadius: 8,
            padding: '24px',
          }}
          className="flex items-center gap-3"
        >
          <CheckCircle2 size={18} style={{ color: '#00C853', flexShrink: 0 }} />
          <div>
            <p style={{ color: '#00C853', fontSize: 12, fontFamily: 'monospace', fontWeight: 700 }}>
              No sanctions matches detected
            </p>
            <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 2 }}>
              Screened against {listCoverage.length > 0 ? listCoverage.length : 'all'} sanctions lists via OpenSanctions
            </p>
          </div>
        </div>
      )}

      {/* Hits table */}
      {hits.length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            SANCTIONS MATCHES ({hits.length})
          </p>
          <div
            style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, overflow: 'hidden' }}
          >
            {/* Header */}
            <div
              className="grid gap-3 px-4 py-2"
              style={{
                gridTemplateColumns: '2fr 80px 100px 1fr',
                background: '#090D13',
                borderBottom: '1px solid #182030',
              }}
            >
              {['ENTITY NAME', 'MATCH TYPE', 'SCORE', 'LISTS MATCHED'].map(h => (
                <span key={h} style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                  {h}
                </span>
              ))}
            </div>
            {hits.map((hit, i) => (
              <div
                key={hit.entity_id ?? i}
                className="grid gap-3 px-4 py-3 items-start"
                style={{
                  gridTemplateColumns: '2fr 80px 100px 1fr',
                  borderBottom: i < hits.length - 1 ? '1px solid #0F1928' : undefined,
                  borderLeft: `3px solid ${MATCH_COLOR[hit.match_type]}`,
                }}
              >
                <span style={{ color: '#DCE8F5', fontSize: 11, fontWeight: 600, wordBreak: 'break-word' }}>
                  {hit.caption}
                </span>
                <MatchTypeBadge matchType={hit.match_type} />
                <div className="flex flex-col gap-1">
                  <span style={{ color: MATCH_COLOR[hit.match_type], fontFamily: 'monospace', fontWeight: 700, fontSize: 11 }}>
                    {Math.round(hit.score * 100)}%
                  </span>
                  <ConfidenceBar value={hit.score} color={MATCH_COLOR[hit.match_type]} height={3} />
                </div>
                <div className="flex flex-wrap gap-1">
                  {hit.datasets.map(ds => (
                    <ListChip key={ds} name={ds} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Highest score indicator */}
      {hits.length > 0 && (
        <div
          style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '10px 14px' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
              HIGHEST MATCH SCORE
            </span>
            <span style={{ color: '#FF1F1F', fontFamily: 'monospace', fontWeight: 800, fontSize: 14 }}>
              {Math.round(highestScore * 100)}%
            </span>
          </div>
          <ConfidenceBar value={highestScore} color="#FF1F1F" height={5} />
        </div>
      )}

      {/* List coverage */}
      {listCoverage.length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 8 }}>
            LISTS SCREENED — OpenSanctions
          </p>
          <div className="flex flex-wrap gap-1.5">
            {listCoverage.map(list => (
              <ScreenedChip key={list} name={list} />
            ))}
          </div>
        </div>
      )}

      {/* Degraded note */}
      {pipeline?.sanctions?.degraded && (
        <div
          style={{ background: '#1A1000', border: '1px solid #F0B42920', borderRadius: 8, padding: '8px 12px' }}
          className="flex items-center gap-2"
        >
          <ShieldAlert size={12} style={{ color: '#F0B429', flexShrink: 0 }} />
          <span style={{ color: '#F0B429', fontSize: 9, fontFamily: 'monospace' }}>
            Sanctions module returned degraded results — manual verification recommended
          </span>
        </div>
      )}
    </motion.div>
  );
}
